import type { Aircraft } from "../aircraft/Aircraft";
import { claimsRunway } from "../aircraft/AircraftState";
import type {
  MutableVec2,
  RunwayDefinition,
  WorldBounds,
} from "../airport/AirportDefinition";
import { distance, pathLength } from "./geometry";

/**
 * Is another aircraft already using this runway, or committed to it?
 *
 * The landing counterpart of `hasDepartureConflict`, and a far blunter
 * instrument: a take-off can be sequenced behind another because the scheduler
 * owns its clock, but an approach is flown by the player, so the only moment
 * two arrivals can be kept apart is the moment the second one is cleared.
 * Hence a whole-runway claim rather than a swept-track test.
 *
 * `exclude` is the aircraft being cleared — an aircraft never blocks itself.
 */
export function isRunwayClaimed(
  runwayId: string,
  aircraft: readonly Aircraft[],
  exclude?: Aircraft,
): boolean {
  return aircraft.some(
    (item) =>
      item !== exclude &&
      item.target?.id === runwayId &&
      claimsRunway(item.state),
  );
}

/**
 * Where a departure leaves the world: straight off the runway axis until it
 * clears the view, plus a margin so it disappears off screen.
 */
export function departureExitPoint(
  runway: RunwayDefinition,
  bounds: WorldBounds,
  margin = 28,
): MutableVec2 {
  const dx = runway.end.x - runway.approach.x;
  const dy = runway.end.y - runway.approach.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;

  const toEdgeX =
    Math.abs(ux) < 1e-6
      ? Number.POSITIVE_INFINITY
      : ((ux > 0 ? bounds.maxX : bounds.minX) - runway.end.x) / ux;
  const toEdgeY =
    Math.abs(uy) < 1e-6
      ? Number.POSITIVE_INFINITY
      : ((uy > 0 ? bounds.maxY : bounds.minY) - runway.end.y) / uy;

  const travel = Math.max(0, Math.min(toEdgeX, toEdgeY)) + margin;
  return { x: runway.end.x + ux * travel, y: runway.end.y + uy * travel };
}

export interface DepartureConflictOptions {
  readonly departureSpeed: number;
  readonly departureAirborneProgress: number;
  readonly conflictFactor: number;
}

/**
 * Would launching `candidate` now run it into an already departing aircraft?
 *
 * Both are modelled as constant-velocity tracks along their runway axes; the
 * closest approach is solved analytically over the window where both are
 * airborne and still inside the world.
 */
export function hasDepartureConflict(
  candidate: Aircraft,
  other: Aircraft,
  bounds: WorldBounds,
  options: DepartureConflictOptions,
): boolean {
  const candidateRunway = candidate.target;
  const otherRunway = other.target;
  if (!candidateRunway || !otherRunway) {
    return false;
  }

  const candidateLength = distance(
    candidateRunway.approach,
    candidateRunway.end,
  );
  const otherLength = distance(otherRunway.approach, otherRunway.end);
  if (candidateLength < 1e-6 || otherLength < 1e-6) {
    return false;
  }

  const speed = options.departureSpeed;
  const candidateVelocity = {
    x:
      ((candidateRunway.end.x - candidateRunway.approach.x) / candidateLength) *
      speed,
    y:
      ((candidateRunway.end.y - candidateRunway.approach.y) / candidateLength) *
      speed,
  };
  const otherVelocity = {
    x: ((otherRunway.end.x - otherRunway.approach.x) / otherLength) * speed,
    y: ((otherRunway.end.y - otherRunway.approach.y) / otherLength) * speed,
  };

  const candidateTrack = [
    candidateRunway.end,
    departureExitPoint(candidateRunway, bounds),
  ];
  const candidateWindow =
    pathLength(candidateRunway.approach, candidateTrack) / speed;
  const otherWindow = pathLength(other.pos, other.path) / speed;

  const progress = options.departureAirborneProgress;
  const airborneAt = Math.max(
    (candidateLength * progress) / speed,
    (Math.max(0, progress - other.takeoffProgress) * otherLength) / speed,
  );
  const horizon = Math.min(candidateWindow, otherWindow);
  if (airborneAt >= horizon) {
    return false;
  }

  const relativePosition = {
    x: candidateRunway.approach.x - other.pos.x,
    y: candidateRunway.approach.y - other.pos.y,
  };
  const relativeVelocity = {
    x: candidateVelocity.x - otherVelocity.x,
    y: candidateVelocity.y - otherVelocity.y,
  };
  const closingSpeedSquared = relativeVelocity.x ** 2 + relativeVelocity.y ** 2;
  const closestAt =
    closingSpeedSquared < 1e-6
      ? airborneAt
      : Math.max(
          airborneAt,
          Math.min(
            horizon,
            -(
              relativePosition.x * relativeVelocity.x +
              relativePosition.y * relativeVelocity.y
            ) / closingSpeedSquared,
          ),
        );

  const separationX = relativePosition.x + relativeVelocity.x * closestAt;
  const separationY = relativePosition.y + relativeVelocity.y * closestAt;
  const minimum =
    (candidate.spec.radius + other.spec.radius) * options.conflictFactor;
  return (
    separationX * separationX + separationY * separationY <= minimum * minimum
  );
}
